import { useEffect } from 'react';
import mermaid from 'mermaid';

interface MermaidRendererProps {
  containerId: string;
}

// Initialize mermaid once globally
let mermaidInitialized = false;

const MermaidRenderer: React.FC<MermaidRendererProps> = ({ containerId }) => {
  useEffect(() => {
    // Only initialize mermaid once globally
    if (!mermaidInitialized) {
      mermaid.initialize({
        startOnLoad: false,
        theme: 'dark',
        fontFamily: 'inherit',
        themeCSS: `
        .cluster-label span p {
          font-size: 1.3rem;
        }
        .labelBkg {
          border-radius: 50%
        }
        .edgeLabel {
          border-radius: 50%
        }
        .edgeLabel p {
          border-radius: 50%
        }
        `,

        flowchart: {
          useMaxWidth: true,
          htmlLabels: true,

        }
      });
      mermaidInitialized = true;
    }

    // Find and render all mermaid diagrams
    const renderDiagrams = async () => {
      const diagrams = document.querySelectorAll(`#${containerId} .mermaid-diagram`);

      // If no diagrams found, nothing to do
      if (diagrams.length === 0) return;

      for (const diagram of Array.from(diagrams)) {
        const diagramId = diagram.getAttribute('data-diagram-id');
        if (!diagramId) continue;

        const code = diagram.textContent || '';

        try {
          // Use timestamp to ensure unique IDs across renders
          const uniqueId = `diagram-${diagramId}-${Date.now()}`;

          // Render the diagram
          const { svg } = await mermaid.render(uniqueId, code);

          // Replace the pre tag with rendered SVG wrapped in a container
          const container = document.createElement('div');
          container.className = 'mermaid-container';
          container.innerHTML = svg;

          // Add rounded corners to all rectangles and edge labels in the diagram
          const rects = container.querySelectorAll('rect, .edgeLabel');
          rects.forEach(rect => {
            rect.setAttribute('rx', '8');
            rect.setAttribute('ry', '8');
          });

          diagram.parentNode?.replaceChild(container, diagram);
        } catch (error) {
          console.error('Mermaid rendering error:', error);
          // Keep the original code block on error
          diagram.classList.add('mermaid-error');
          const errorDiv = document.createElement('div');
          errorDiv.className = 'mermaid-error-message';
          errorDiv.textContent = 'Error rendering diagram';
          diagram.parentNode?.insertBefore(errorDiv, diagram);
        }
      }
    };

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      renderDiagrams();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [containerId]);

  return null;
};

export default MermaidRenderer;
